/**
 * Dictionary mapping code-ready field names to user-friendly
 * display names, descriptions, and visibility flags.
 *
 * Applies to both DNO Configuration and Licence Area Threshold fields.
 *
 * - `displayName`: Friendly label shown in the UI columns and pills.
 * - `description`: Tooltip text shown on hover over the field name.
 */

export type ThresholdValueType = "boolean" | "number"

type FieldMeta = {
    displayName: string
    description: string
    category?: string
    valueType: ThresholdValueType
}

/**
 * Add entries here as new fields appear in DynamoDB.
 * Any field NOT present in this dictionary will fall back to
 * its raw code name with no tooltip.
 */
const THRESHOLD_DICTIONARY: Record<string, FieldMeta> = {
    // --- DNO Configuration fields ---
    trustInstaller: {
        displayName: "Trust Installer on Looped Supply",
        description:
            "If true, the DNO will trust installer-provided data for looped supply connections.",
        category: "Assessment Configuration",
        valueType: "boolean",
    },
    approveSgi2: {
        displayName: "Approve SGI-2",
        description:
            "If true, the DNO will approve SGI-2 compliant connections.",
        category: "Assessment Configuration",
        valueType: "boolean",
    },
    sendEmailsToCustomers: {
        displayName: "Send Emails to Customers",
        description:
            "If true, email notifications will be sent to customers for relevant events.",
        category: "Notification Settings",
        valueType: "boolean",
    },

    // --- Licence Area Threshold fields ---

    sendExMpanEmail: {
        displayName: "Send Ex-MPAN Email",
        description:
            "If true, an email notification is sent when Export MPAN is provided.",
        category: "Notification Settings",
        valueType: "boolean",
    },

    blackPlasticAssumedCutoutRatingSysAmps: {
        displayName: "Black Plastic Assumed Cutout Rating (A)",
        description:
            "Assumed cutout rating for black plastic cutouts, in system amps.",
        category: "Black Plastic General",
        valueType: "number",
    },
    blackPlasticPropertyMaxDemandThresholdSysAmps: {
        displayName: "Black Plastic Property Max Demand Threshold (A)",
        description:
            "Property Maximum demand threshold for properties with black plastic cutouts, in system amps.",
        category: "Black Plastic General",
        valueType: "number",
    },
    greyPlasticAssumedCutoutRatingSysAmps: {
        displayName: "Grey Plastic Assumed Cutout Rating (A)",
        description:
            "Assumed cutout rating for grey plastic cutouts, in system amps.",
        category: "Grey Plastic General",
        valueType: "number",
    },
    greyPlasticPropertyMaxDemandThresholdSysAmps: {
        displayName: "Grey Plastic Property Max Demand Threshold (A)",
        description:
            "Property Maximum demand threshold for properties with grey plastic cutouts, in system amps.",
        category: "Grey Plastic General",
        valueType: "number",
    },
    metalCladAssumedCutoutRatingSysAmps: {
        displayName: "Metal Clad Assumed Cutout Rating (A)",
        description:
            "Assumed cutout rating for metal clad cutouts, in system amps.",
        category: "Metal Clad General",
        valueType: "number",
    },
    metalCladPropertyMaxDemandThresholdSysAmps: {
        displayName: "Metal Clad Property Max Demand Threshold (A)",
        description:
            "Property Maximum demand threshold for properties with metal clad cutouts, in system amps.",
        category: "Metal Clad General",
        valueType: "number",
    },

    // CT escalation
    ctEscalateLevelPercentageImportCapacity: {
        displayName: "CT Escalate Level (% Import Capacity)",
        description:
            "Percentage of import capacity at which CT escalation is triggered.",
        category: "Assessment Configuration",
        valueType: "number",
    },

    // CX escalation
    cxEscalateThresholdSinglePhasekwPerPhase: {
        displayName: "CX Escalate Threshold (Single Phase, kW per Phase)",
        description:
            "Commissioning escalation threshold for single-phase supply, in kW per phase.",
        category: "Assessment Configuration",
        valueType: "number",
    },
    cxEscalateThresholdThreePhasekwPerPhase: {
        displayName: "CX Escalate Threshold (Three Phase, kW per Phase)",
        description:
            "Commissioning escalation threshold for three-phase supply, in kW per phase.",
        category: "Assessment Configuration",
        valueType: "number",
    },

    // Generation thresholds — by cutout type
    genBlackPlasticThresholdSinglePhasekwPerPhase: {
        displayName: "Gen Black Plastic Threshold (Single Phase, kW per Phase)",
        description:
            "Generation threshold for single-phase with black plastic cutout, in kW per phase.",
        category: "Black Plastic Generation",
        valueType: "number",
    },
    genBlackPlasticThresholdThreePhasekwPerPhase: {
        displayName: "Gen Black Plastic Threshold (Three Phase, kW per Phase)",
        description:
            "Generation threshold for three-phase with black plastic cutout, in kW per phase.",
        category: "Black Plastic Generation",
        valueType: "number",
    },
    genGreyPlasticThresholdSinglePhasekwPerPhase: {
        displayName: "Gen Grey Plastic Threshold (Single Phase, kW per Phase)",
        description:
            "Generation threshold for single-phase with grey plastic cutout, in kW per phase.",
        category: "Grey Plastic Generation",
        valueType: "number",
    },
    genGreyPlasticThresholdThreePhasekwPerPhase: {
        displayName: "Gen Grey Plastic Threshold (Three Phase, kW per Phase)",
        description:
            "Generation threshold for three-phase with grey plastic cutout, in kW per phase.",
        category: "Grey Plastic Generation",
        valueType: "number",
    },
    genMetalCladThresholdSinglePhasekwPerPhase: {
        displayName: "Gen Metal Clad Threshold (Single Phase, kW per Phase)",
        description:
            "Generation threshold for single-phase with metal clad cutout, in kW per phase.",
        category: "Metal Clad Generation",
        valueType: "number",
    },
    genMetalCladThresholdThreePhasekwPerPhase: {
        displayName: "Gen Metal Clad Threshold (Three Phase, kW per Phase)",
        description:
            "Generation threshold for three-phase with metal clad cutout, in kW per phase.",
        category: "Metal Clad Generation",
        valueType: "number",
    },

    // EV charger (EVAC) thresholds — by cutout type
    evacBlackPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName: "EVAC Black Plastic Threshold (Single Phase, A)",
        description:
            "EV charger threshold for single-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic EVAC",
        valueType: "number",
    },
    evacBlackPlasticThresholdThreePhaseTotalSysAmps: {
        displayName: "EVAC Black Plastic Threshold (Three Phase, A)",
        description:
            "EV charger threshold for three-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic EVAC",
        valueType: "number",
    },
    evacGreyPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName: "EVAC Grey Plastic Threshold (Single Phase, A)",
        description:
            "EV charger threshold for single-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic EVAC",
        valueType: "number",
    },
    evacGreyPlasticThresholdThreePhaseTotalSysAmps: {
        displayName: "EVAC Grey Plastic Threshold (Three Phase, A)",
        description:
            "EV charger threshold for three-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic EVAC",
        valueType: "number",
    },
    evacMetalCladThresholdSinglePhaseTotalSysAmps: {
        displayName: "EVAC Metal Clad Threshold (Single Phase, A)",
        description:
            "EV charger threshold for single-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad EVAC",
        valueType: "number",
    },
    evacMetalCladThresholdThreePhaseTotalSysAmps: {
        displayName: "EVAC Metal Clad Threshold (Three Phase, A)",
        description:
            "EV charger threshold for three-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad EVAC",
        valueType: "number",
    },

    // Heat pump (HP) thresholds — by cutout type
    hpBlackPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName: "HP Black Plastic Threshold (Single Phase, A)",
        description:
            "Heat pump threshold for single-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic HP",
        valueType: "number",
    },
    hpBlackPlasticThresholdThreePhaseTotalSysAmps: {
        displayName: "HP Black Plastic Threshold (Three Phase, A)",
        description:
            "Heat pump threshold for three-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic HP",
        valueType: "number",
    },
    hpGreyPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName: "HP Grey Plastic Threshold (Single Phase, A)",
        description:
            "Heat pump threshold for single-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic HP",
        valueType: "number",
    },
    hpGreyPlasticThresholdThreePhaseTotalSysAmps: {
        displayName: "HP Grey Plastic Threshold (Three Phase, A)",
        description:
            "Heat pump threshold for three-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic HP",
        valueType: "number",
    },
    hpMetalCladThresholdSinglePhaseTotalSysAmps: {
        displayName: "HP Metal Clad Threshold (Single Phase, A)",
        description:
            "Heat pump threshold for single-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad HP",
        valueType: "number",
    },
    hpMetalCladThresholdThreePhaseTotalSysAmps: {
        displayName: "HP Metal Clad Threshold (Three Phase, A)",
        description:
            "Heat pump threshold for three-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad HP",
        valueType: "number",
    },
    clusterCheckDemandApplicationCount: {
        displayName: "Cluster Check Demand Application Count",
        description:
            "The number of demand applications before an application will fail due to cluster check.",
        category: "Cluster Check Demand",
        valueType: "number",
    },
    clusterCheckDemandTimePeriodDays: {
        displayName: "Cluster Check Demand Time Period (Days)",
        description:
            "The time period in days for checking demand applications before an application will fail due to cluster check.",
        category: "Cluster Check Demand",
        valueType: "number",
    },
    clusterCheckGenApplicationCount: {
        displayName: "Cluster Check Generation Application Count",
        description:
            "The number of generation applications before an application will fail due to cluster check.",
        category: "Cluster Check Generation",
        valueType: "number",
    },
    clusterCheckGenTimePeriodDays: {
        displayName: "Cluster Check Generation Time Period (Days)",
        description:
            "The time period in days for checking generation applications before an application will fail due to cluster check.",
        category: "Cluster Check Generation",
        valueType: "number",
    },
    ebBlackPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName:
            "Electric Boiler Black Plastic Threshold (Single Phase, A)",
        description:
            "Electric Boiler threshold for single-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic Electric Boiler",
        valueType: "number",
    },
    ebBlackPlasticThresholdThreePhaseTotalSysAmps: {
        displayName: "Electric Boiler Black Plastic Threshold (Three Phase, A)",
        description:
            "Electric Boiler threshold for three-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic Electric Boiler",
        valueType: "number",
    },
    ebGreyPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName: "Electric Boiler Grey Plastic Threshold (Single Phase, A)",
        description:
            "Electric Boiler threshold for single-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic Electric Boiler",
        valueType: "number",
    },
    ebGreyPlasticThresholdThreePhaseTotalSysAmps: {
        displayName: "Electric Boiler Grey Plastic Threshold (Three Phase, A)",
        description:
            "Electric Boiler threshold for three-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic Electric Boiler",
        valueType: "number",
    },
    ebMetalCladThresholdSinglePhaseTotalSysAmps: {
        displayName: "Electric Boiler Metal Clad Threshold (Single Phase, A)",
        description:
            "Electric Boiler threshold for single-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad Electric Boiler",
        valueType: "number",
    },
    ebMetalCladThresholdThreePhaseTotalSysAmps: {
        displayName: "Electric Boiler Metal Clad Threshold (Three Phase, A)",
        description:
            "Electric Boiler threshold for three-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad Electric Boiler",
        valueType: "number",
    },
    acuBlackPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName:
            "Air Conditioning Unit Black Plastic Threshold (Single Phase, A)",
        description:
            "Air Conditioning Unit threshold for single-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic Air Conditioning Unit",
        valueType: "number",
    },
    acuBlackPlasticThresholdThreePhaseTotalSysAmps: {
        displayName:
            "Air Conditioning Unit Black Plastic Threshold (Three Phase, A)",
        description:
            "Air Conditioning Unit threshold for three-phase with black plastic cutout, in total system amps.",
        category: "Black Plastic Air Conditioning Unit",
        valueType: "number",
    },
    acuGreyPlasticThresholdSinglePhaseTotalSysAmps: {
        displayName:
            "Air Conditioning Unit Grey Plastic Threshold (Single Phase, A)",
        description:
            "Air Conditioning Unit threshold for single-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic Air Conditioning Unit",
        valueType: "number",
    },
    acuGreyPlasticThresholdThreePhaseTotalSysAmps: {
        displayName:
            "Air Conditioning Unit Grey Plastic Threshold (Three Phase, A)",
        description:
            "Air Conditioning Unit threshold for three-phase with grey plastic cutout, in total system amps.",
        category: "Grey Plastic Air Conditioning Unit",
        valueType: "number",
    },
    acuMetalCladThresholdSinglePhaseTotalSysAmps: {
        displayName:
            "Air Conditioning Unit Metal Clad Threshold (Single Phase, A)",
        description:
            "Air Conditioning Unit threshold for single-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad Air Conditioning Unit",
        valueType: "number",
    },
    acuMetalCladThresholdThreePhaseTotalSysAmps: {
        displayName:
            "Air Conditioning Unit Metal Clad Threshold (Three Phase, A)",
        description:
            "Air Conditioning Unit threshold for three-phase with metal clad cutout, in total system amps.",
        category: "Metal Clad Air Conditioning Unit",
        valueType: "number",
    },
}

/**
 * Returns the display name for a given field code name.
 * Falls back to the raw code name if not found.
 *
 * Parameters
 * ----------
 * codeName : string
 *     The raw field name from DynamoDB / API.
 *
 * Returns
 * -------
 * string
 *     User-friendly display name.
 */
export const getDisplayName = (codeName: string): string =>
    THRESHOLD_DICTIONARY[codeName]?.displayName ?? codeName

/**
 * Returns the description (tooltip text) for a given field.
 * Returns an empty string if not found or not set.
 *
 * Parameters
 * ----------
 * codeName : string
 *     The raw field name from DynamoDB / API.
 *
 * Returns
 * -------
 * string
 *     Tooltip description, or empty string.
 */
export const getFieldDescription = (codeName: string): string =>
    THRESHOLD_DICTIONARY[codeName]?.description ?? ""

export const getFieldCategory = (codeName: string): string =>
    THRESHOLD_DICTIONARY[codeName]?.category ?? "Uncategorized"

/**
 * Returns the expected value type for a given field.
 * Returns undefined if the field is not in the dictionary.
 *
 * Parameters
 * ----------
 * codeName : string
 *     The raw field name from DynamoDB / API.
 *
 * Returns
 * -------
 * ThresholdValueType | undefined
 *     "boolean" or "number", or undefined if unknown.
 */
export const getExpectedType = (
    codeName: string
): ThresholdValueType | undefined => THRESHOLD_DICTIONARY[codeName]?.valueType

export type NewValueError = {
    field: string
    message: string
    value: unknown
}

/**
 * Validates a newValue for a threshold / config field.
 *
 * Rules
 * -----
 * - Boolean fields: value must be `true` or `false`.
 * - Number fields: value must be exactly -1, 0, or any
 *   positive number (decimals allowed).
 *   Negative numbers other than -1 are rejected.
 * - Unknown fields (not in dictionary) are skipped (valid).
 * - `null` is always rejected for a known field.
 *
 * Parameters
 * ----------
 * field : string
 *     The raw field name from DynamoDB / API.
 * value : unknown
 *     The newValue to validate.
 *
 * Returns
 * -------
 * NewValueError | null
 *     An error object if invalid, or null if valid.
 */
export const validateNewValue = (
    field: string,
    value: unknown
): NewValueError | null => {
    const expected = getExpectedType(field)

    if (expected === undefined) {
        return {
            field,
            message: `${field} is not a recognised field`,
            value,
        }
    }

    if (value === null || value === undefined) {
        return {
            field,
            message: `${field} must not be null`,
            value,
        }
    }

    if (expected === "boolean") {
        if (typeof value !== "boolean") {
            return {
                field,
                message: `${field} must be a boolean`,
                value,
            }
        }
        return null
    }

    // expected === "number"
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return {
            field,
            message: `${field} must be a finite number`,
            value,
        }
    }

    if (value !== -1 && value < 0) {
        return {
            field,
            message: `${field} must be -1, 0, or a positive number`,
            value,
        }
    }

    return null
}

export default THRESHOLD_DICTIONARY
